import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  PlayArrow as TestIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import {
  fetchAllPrompts,
  fetchPromptStats,
  searchPrompts,
  deletePrompt,
  fetchDistinctUseCases,
  fetchDistinctPromptTypes,
  fetchDistinctDifficulty,
  fetchPromptsByUseCase,
  fetchPromptsByType,
  fetchPromptsByDifficulty,
} from './llm-prompts.service';
import LLMPromptEditor from './LLMPromptEditor';
import LLMPromptVersions from './LLMPromptVersions';
import LLMPromptPreview from './LLMPromptPreview';

import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-material.css';

// ─── Custom ag-grid header styles ─────────────────────────────────────────
const gridContainerStyles = {
  '& .ag-header': {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ddd',
  },
  '& .ag-header-cell-text': {
    color: '#333',
    fontWeight: 700,
    fontSize: '13px',
  },
  '& .ag-header-cell': {
    borderRight: '1px solid #e0e0e0',
  },
};

// ─── Prompt type colours ──────────────────────────────────────────────────
const typeColour = {
  SYSTEM_MESSAGE: '#3366FF',
  USER_PROMPT: '#22c55e',
  TEMPLATE: '#ea580c',
};

const useCaseColour = {
  QUESTION_GENERATION: '#7c3aed',
  QUESTION_REPHRASING: '#06b6d4',
  QUESTION_CLASSIFICATION: '#3366FF',
  SEARCH_RE_RANKING: '#f59e0b',
  CLINICAL_QUIZ_GENERATION: '#dc3545',
};

// ─── Main Component ───────────────────────────────────────────────────────
const LLMPrompts = () => {
  // Data state
  const [prompts, setPrompts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filter state — searchInput is the live text field; searchTerm is committed on Enter
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [useCaseFilter, setUseCaseFilter] = useState('');
  const [promptTypeFilter, setPromptTypeFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');

  // Distinct values for filter dropdowns
  const [distinctUseCases, setDistinctUseCases] = useState([]);
  const [distinctPromptTypes, setDistinctPromptTypes] = useState([]);
  const [distinctDifficulty, setDistinctDifficulty] = useState([]);

  // Dialog state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editPrompt, setEditPrompt] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionsKey, setVersionsKey] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);

  // ─── Load data ──────────────────────────────────────────────────────────
  const loadPrompts = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      // Apply server-side filters if set (only one at a time — priority: search > useCase > type > difficulty)
      if (searchTerm.trim()) {
        res = await searchPrompts(searchTerm.trim());
      } else if (useCaseFilter) {
        res = await fetchPromptsByUseCase(useCaseFilter);
      } else if (promptTypeFilter) {
        res = await fetchPromptsByType(promptTypeFilter);
      } else if (difficultyFilter) {
        res = await fetchPromptsByDifficulty(difficultyFilter);
      } else {
        res = await fetchAllPrompts();
      }
      setPrompts(res?.result || []);
    } catch (e) {
      toast.error('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, useCaseFilter, promptTypeFilter, difficultyFilter]);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetchPromptStats();
      setStats(res?.result || null);
    } catch (_) { /* stats are optional */ }
  }, []);

  const loadFilterOptions = useCallback(async () => {
    try {
      const [ucRes, ptRes, dfRes] = await Promise.all([
        fetchDistinctUseCases(),
        fetchDistinctPromptTypes(),
        fetchDistinctDifficulty(),
      ]);
      setDistinctUseCases(ucRes?.result || []);
      setDistinctPromptTypes(ptRes?.result || []);
      setDistinctDifficulty(dfRes?.result || []);
    } catch (_) { /* filters are optional */ }
  }, []);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  useEffect(() => {
    loadStats();
    loadFilterOptions();
  }, [loadStats, loadFilterOptions]);

  const handleRefresh = () => {
    loadPrompts();
    loadStats();
  };

  // ─── Filter helpers ─────────────────────────────────────────────────────
  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setUseCaseFilter('');
    setPromptTypeFilter('');
    setDifficultyFilter('');
  };

  const hasFilters = searchTerm || useCaseFilter || promptTypeFilter || difficultyFilter;

  // ─── CRUD handlers ──────────────────────────────────────────────────────
  const handleCreate = () => {
    setEditPrompt(null);
    setEditorOpen(true);
  };

  const handleEdit = (prompt) => {
    setEditPrompt(prompt);
    setEditorOpen(true);
  };

  const handleViewVersions = (promptKey) => {
    setVersionsKey(promptKey);
    setVersionsOpen(true);
  };

  const handleTestTemplate = (prompt) => {
    setPreviewPrompt(prompt);
    setPreviewOpen(true);
  };

  const handleDeleteConfirm = (prompt) => {
    setPromptToDelete(prompt);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deletePrompt(promptToDelete.promptId);
      toast.success('Prompt deleted');
      setDeleteDialogOpen(false);
      setPromptToDelete(null);
      handleRefresh();
    } catch (e) {
      toast.error(e || 'Failed to delete prompt');
    }
  };

  const handleSaved = () => {
    handleRefresh();
    loadFilterOptions();
  };

  // ─── ag-grid column definitions ─────────────────────────────────────────
  const columnDefs = [
    {
      headerName: 'Prompt Key',
      field: 'promptKey',
      filter: true,
      sortable: true,
      minWidth: 240,
      cellRendererFramework: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      headerName: 'Type',
      field: 'promptType',
      filter: true,
      sortable: true,
      width: 160,
      cellRendererFramework: (params) => (
        <Chip
          label={params.value?.replace(/_/g, ' ')}
          size="small"
          sx={{ bgcolor: typeColour[params.value] || '#666', color: '#fff', fontSize: 11 }}
        />
      ),
    },
    {
      headerName: 'Use Case',
      field: 'useCase',
      filter: true,
      sortable: true,
      width: 200,
      cellRendererFramework: (params) => (
        <Chip
          label={params.value?.replace(/_/g, ' ')}
          size="small"
          variant="outlined"
          sx={{ borderColor: useCaseColour[params.value] || '#666', color: useCaseColour[params.value] || '#666', fontSize: 11 }}
        />
      ),
    },
    {
      headerName: 'Question Type',
      field: 'questionType',
      filter: true,
      sortable: true,
      width: 150,
      cellRendererFramework: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      headerName: 'Difficulty',
      field: 'difficultyLevel',
      filter: true,
      sortable: true,
      width: 120,
      cellRendererFramework: (params) => params.value ? (
        <Chip label={params.value} size="small" variant="outlined" />
      ) : '-',
    },
    {
      headerName: 'Ver',
      field: 'version',
      width: 80,
      sortable: true,
      cellRendererFramework: (params) => (
        <Typography variant="body2" fontWeight={600}>v{params.value}</Typography>
      ),
    },
    {
      headerName: 'Active',
      field: 'isActive',
      width: 90,
      sortable: true,
      cellRendererFramework: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      headerName: 'Content Preview',
      field: 'promptContent',
      flex: 1,
      minWidth: 200,
      cellRendererFramework: (params) => (
        <Tooltip title={params.value?.substring(0, 500) || ''}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              fontFamily: 'monospace',
              fontSize: 12,
            }}
          >
            {params.value?.substring(0, 120) || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      headerName: 'Actions',
      pinned: 'right',
      width: 180,
      sortable: false,
      filter: false,
      cellRendererFramework: (params) => (
        <Box display="flex" gap={0.5} alignItems="center">
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.data)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Version History">
            <IconButton size="small" onClick={() => handleViewVersions(params.data.promptKey)}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.data.promptType === 'TEMPLATE' && (
            <Tooltip title="Test Template">
              <IconButton size="small" color="primary" onClick={() => handleTestTemplate(params.data)}>
                <TestIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDeleteConfirm(params.data)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const defaultColDef = {
    resizable: true,
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Typography variant="h5" fontWeight={600} mb={1}>
        LLM Prompts Management
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        View, create, and edit system messages, user prompts, and templates used by AI features.
      </Typography>

      {/* Stats strip */}
      {stats && (
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <Chip label={`Total: ${stats.totalPrompts ?? '-'}`} variant="outlined" />
          <Chip label={`Active: ${stats.activePrompts ?? '-'}`} color="success" variant="outlined" />
          <Chip
            label={`Inactive: ${(stats.totalPrompts != null && stats.activePrompts != null) ? stats.totalPrompts - stats.activePrompts : '-'}`}
            variant="outlined"
          />
          <Chip label={`Prompt Keys: ${stats.totalPromptKeys ?? '-'}`} variant="outlined" />
        </Box>
      )}

      {/* Filters row */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          placeholder="Search prompts... (press Enter)"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearchTerm(searchInput);
              setUseCaseFilter('');
              setPromptTypeFilter('');
              setDifficultyFilter('');
            }
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Use Case</InputLabel>
          <Select
            value={useCaseFilter}
            label="Use Case"
            onChange={(e) => { setUseCaseFilter(e.target.value); setPromptTypeFilter(''); setDifficultyFilter(''); setSearchTerm(''); setSearchInput(''); }}
          >
            <MenuItem value="">All</MenuItem>
            {distinctUseCases.map((uc) => (
              <MenuItem key={uc} value={uc}>{uc.replace(/_/g, ' ')}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Prompt Type</InputLabel>
          <Select
            value={promptTypeFilter}
            label="Prompt Type"
            onChange={(e) => { setPromptTypeFilter(e.target.value); setUseCaseFilter(''); setDifficultyFilter(''); setSearchTerm(''); setSearchInput(''); }}
          >
            <MenuItem value="">All</MenuItem>
            {distinctPromptTypes.map((pt) => (
              <MenuItem key={pt} value={pt}>{pt.replace(/_/g, ' ')}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Difficulty</InputLabel>
          <Select
            value={difficultyFilter}
            label="Difficulty"
            onChange={(e) => { setDifficultyFilter(e.target.value); setUseCaseFilter(''); setPromptTypeFilter(''); setSearchTerm(''); setSearchInput(''); }}
          >
            <MenuItem value="">All</MenuItem>
            {distinctDifficulty.map((d) => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {hasFilters && (
          <Tooltip title="Clear all filters">
            <IconButton size="small" onClick={clearFilters}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Box flex={1} />
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh}><RefreshIcon /></IconButton>
        </Tooltip>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Create Prompt
        </Button>
      </Box>

      {/* Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : prompts.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {hasFilters
            ? 'No prompts match the current filters.'
            : 'No prompts found. Create your first prompt to get started.'}
        </Alert>
      ) : (
        <Box className="ag-theme-material" sx={{ width: '100%', height: 'calc(100vh - 320px)', minHeight: 400, ...gridContainerStyles }}>
          <AgGridReact
            rowData={prompts}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={15}
            animateRows={true}
            rowHeight={52}
            headerHeight={48}
            suppressCellSelection={true}
            getRowNodeId={(data) => String(data.promptId)}
          />
        </Box>
      )}

      {/* Dialogs */}
      <LLMPromptEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={handleSaved}
        editPrompt={editPrompt}
        distinctUseCases={distinctUseCases}
        distinctPromptTypes={distinctPromptTypes}
        distinctDifficulty={distinctDifficulty}
      />

      <LLMPromptVersions
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        promptKey={versionsKey}
        onChanged={handleSaved}
      />

      <LLMPromptPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        prompt={previewPrompt}
      />

      {/* Delete confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Prompt</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{promptToDelete?.promptKey}</strong> (v{promptToDelete?.version})?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LLMPrompts;
